import React, {MouseEvent, ReactNode, useEffect, useMemo, useState} from 'react';
import {Squash as Hamburger} from 'hamburger-react';
import {AiFillHome, AiOutlineTeam} from 'react-icons/ai';
import {BiCalendar, BiChevronDown, BiNews} from 'react-icons/bi';
import {RiTeamFill} from 'react-icons/ri';
import {NavLink, useLocation} from 'react-router-dom';
import logo from '../../assets/wimpel.webp';
import {useSiteConfig} from '../../data/ConfigContext';
import {SubLink} from '../../types';
import {teamPath} from '../../utils/slug';
import {STATIC_PAGES} from '../staticPages';
import './navbar.css';

interface MenuItem {
    name: string;
    icon: ReactNode;
    /** items without a path group the `links` below them */
    path?: string;
    links?: SubLink[];
}

const CLUB_LINKS: SubLink[] = STATIC_PAGES
    .filter(page => page.clubMenu)
    .map(page => ({name: page.title, link: page.path}));

const Navbar = () => {
    const [menuOpen, setMenuOpen] = useState(false);
    const {pathname} = useLocation();
    const config = useSiteConfig();

    useEffect(() => {
        setMenuOpen(false);
    }, [pathname]);

    // the page behind the open mobile menu must not scroll
    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    const menu = useMemo<MenuItem[]>(() => [
        {name: 'Home', path: '/', icon: <AiFillHome/>},
        {name: 'News', path: '/news', icon: <BiNews/>},
        {name: 'Termine', path: '/termine', icon: <BiCalendar/>},
        {
            name: 'Teams',
            icon: <AiOutlineTeam/>,
            links: (config?.tables ?? []).map(team => ({name: team.name, link: teamPath(team.internal_name)})),
        },
        {name: 'Verein', icon: <RiTeamFill/>, links: CLUB_LINKS},
    ], [config]);

    const groupActive = (item: MenuItem) => !!item.links?.some(link => pathname.startsWith(link.link));

    // also closes the menu when the link to the current page is clicked
    const closeMobileMenu = (event: MouseEvent) => {
        if ((event.target as HTMLElement).closest('a')) setMenuOpen(false);
    };

    // a clicked dropdown link would otherwise keep the dropdown open via :focus-within
    const closeDropdown = () => (document.activeElement as HTMLElement | null)?.blur();

    return (
        <header className="navbar">
            <div className="navbar-inner">
                <NavLink to="/" className="navbar-logo" aria-label="Startseite">
                    <img src={logo} alt="VCH Logo"/>
                </NavLink>

                <nav className="navbar-links" aria-label="Hauptmenü">
                    {menu.map(item => item.path ? (
                        <NavLink key={item.name} to={item.path} end className="navbar-link">{item.name}</NavLink>
                    ) : (
                        <div key={item.name} className="navbar-dropdown">
                            <button type="button" className={`navbar-link${groupActive(item) ? ' is-active' : ''}`}
                                    aria-haspopup="true">
                                {item.name} <BiChevronDown aria-hidden="true"/>
                            </button>
                            <div className="navbar-dropdown-menu" onClick={closeDropdown}>
                                {item.links?.map(link => <NavLink key={link.link} to={link.link}>{link.name}</NavLink>)}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="navbar-toggle">
                    <Hamburger size={22} color="#fff" toggled={menuOpen} toggle={setMenuOpen} label="Menü"/>
                </div>
            </div>

            {menuOpen && (
                <nav className="mobile-menu" aria-label="Menü" onClick={closeMobileMenu}>
                    {menu.map(item => item.path ? (
                        <NavLink key={item.name} to={item.path} end className="mobile-menu-link">
                            {item.icon}
                            {item.name}
                        </NavLink>
                    ) : (
                        <div key={item.name} className="mobile-menu-group">
                            <div className="mobile-menu-heading">
                                {item.icon}
                                {item.name}
                            </div>
                            <div className="mobile-menu-sublinks">
                                {item.links?.map(link => <NavLink key={link.link} to={link.link}>{link.name}</NavLink>)}
                            </div>
                        </div>
                    ))}
                </nav>
            )}
        </header>
    );
};

export default Navbar;
